import { useEffect, useRef, useState, Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function NewNavigation({ paths }) {
  const { pathname } = useLocation();

  // Track scrollbar thumb
  const [thumbStyle, setThumbStyle] = useState({ left: '0%', width: '0%' });
  const containerRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const { scrollWidth, clientWidth, scrollLeft } = el;
      const overflowing = scrollWidth > clientWidth;
      setIsOverflowing(overflowing);

      if (overflowing) {
        const widthPct = (clientWidth / scrollWidth) * 100;
        const offsetPct =
          (scrollLeft / (scrollWidth - clientWidth)) * (100 - widthPct);
        setThumbStyle({
          width: `${widthPct}%`,
          left: `${offsetPct}%`,
        });
      }
    };

    update();
    el.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div className="relative">
      {/* Scroll container */}
      <div
        ref={containerRef}
        className={`scrollbar-custom hide-bar max-w-full overflow-x-auto 
          bg-[var(--bg-surface)] border border-[var(--border)] 
          ${isOverflowing ? 'rounded-b-0 rounded-t-lg' : 'rounded-lg'}`}
      >
        <div className="flex w-full justify-between gap-2 p-2 whitespace-nowrap">
          {/* Render nav items */}
          {paths.map(([label, pathKey]) => (
            <Fragment key={pathKey}>
              {pathname.includes(pathKey) && (
                <h1 className="hidden">
                  League of Legends {label}
                </h1>
              )}
              <Link
                to={`/${pathKey}`}
                className={`
                  relative px-4 py-2 text-sm font-semibold
                  transition-colors duration-300
                  before:content-[''] before:absolute before:bottom-0
                  before:left-1/2 before:-translate-x-1/2
                  before:h-[1px] before:w-0 before:bg-[#00BAFF]
                  before:transition-all before:duration-300
                  ${pathname.includes(pathKey)
                    ? 'text-[#00BAFF] before:w-[55%]'
                    : 'text-[#fff] hover:text-[#00BAFF] hover:before:w-[50%]'}
                `}
              >
                {label}
              </Link>
            </Fragment>
          ))}
        </div>
      </div>

      {/* Left gradient fade */}
      {isOverflowing && (
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-10
            bg-gradient-to-r from-[var(--bg-surface)] to-transparent
            sm:hidden rounded-b-0 rounded-t-lg"
        />
      )}

      {/* Right gradient fade */}
      {isOverflowing && (
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-10
            bg-gradient-to-l from-[var(--bg-surface)] to-transparent
            sm:hidden rounded-b-0 rounded-t-lg"
        />
      )}

      {/* Custom thumb indicator */}
      {isOverflowing && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[var(--bg-surface-variant)] pointer-events-none rounded-b-0 rounded-t-lg">
          <div
            className="absolute h-full bg-[#00BAFF] transition-all duration-150 ease-out"
            style={{
              width: thumbStyle.width,
              left: thumbStyle.left,
            }}
          />
        </div>
      )}
    </div>
  );
}
